// RHF migration
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { Label } from "@/shared/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/shared/ui/form";
import type { CoachingScenarioItem, TaskCreateRequest } from "../../api/coachingService";
import { isPracticableScenario } from "./helpers";
import { memberService } from "@/entities/member";

type Props = {
  open: boolean;
  scenarios: CoachingScenarioItem[];
  onClose: () => void;
  onSubmit: (data: TaskCreateRequest) => void;
  isPending: boolean;
};

const taskCreateSchema = z.object({
  name: z.string().trim().min(1, "任务名称必填"),
  scenarioId: z.string().min(1, "请选择对练场景"),
  deadline: z.string(),
  // 留空 = 按系统默认 60。填 0 是合法的(这次全员通过),所以校验只管 0-100 的范围,
  // 空串才代表"没指定" —— 用 "" 而不是 0 当哨兵值。
  passScore: z
    .string()
    .refine((v) => v === "" || (/^\d+$/.test(v) && Number(v) >= 0 && Number(v) <= 100), {
      message: "及格线填 0-100,留空按默认 60",
    }),
  // 同理:留空 = 按场景配的走。轮数没有"0 也合法"的问题(0 轮 = 开口就收尾),但判据仍用空串,
  // 和及格线保持同一套写法,免得下次有人照着改错。
  maxRounds: z
    .string()
    .refine((v) => v === "" || (/^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 50), {
      message: "轮数填 1-50,留空按场景",
    }),
});

type TaskCreateValues = z.infer<typeof taskCreateSchema>;

export function TaskCreateDialog({ open, scenarios, onClose, onSubmit, isPending }: Props) {
  const { t } = useTranslation("coaching");
  const form = useForm<TaskCreateValues>({
    resolver: zodResolver(taskCreateSchema),
    mode: "onBlur",
    defaultValues: {
      name: "",
      scenarioId: "",
      deadline: "",
      passScore: "",
      maxRounds: "",
    },
  });

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      form.reset({ name: "", scenarioId: "", deadline: "", passScore: "", maxRounds: "" });
      setSelectedIds(new Set());
      setSearch("");
    }
  }, [open, form]);

  const { data: memberList } = useQuery({
    queryKey: ["members", "for-task"],
    queryFn: () => memberService.list(),
    enabled: open,
    staleTime: 60_000,
  });

  const members = useMemo(() => memberList ?? [], [memberList]);

  const filteredMembers = useMemo(() => {
    if (!search.trim()) return members;
    const q = search.toLowerCase();
    return members.filter(
      (m) =>
        (m.name ?? "").toLowerCase().includes(q) ||
        (m.department ?? "").toLowerCase().includes(q) ||
        (m.position ?? "").toLowerCase().includes(q)
    );
  }, [members, search]);

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredMembers.forEach((m) => next.add(m.principalId));
      return next;
    });
  };

  const clearAll = () => setSelectedIds(new Set());

  const handleSubmit = (values: TaskCreateValues) => {
    if (selectedIds.size === 0) return;
    onSubmit({
      name: values.name.trim(),
      scenarioId: parseInt(values.scenarioId),
      // ★datetime-local 给的就是本地墙钟(2026-10-26T18:00),原样补秒送。此前先 toISOString() 转成 UTC
      //   再切掉「Z」,后端当本地时间存 → 18:00 落成 10:00(1025 陪练舱录片实录)。
      deadline: values.deadline ? `${values.deadline.slice(0, 16)}:00` : undefined,
      targetMemberIds: Array.from(selectedIds),
      // 空串 → 不传(后端落 null = 没指定);"0" → 传 0,不能被 falsy 判掉
      passScore: values.passScore === "" ? undefined : Number(values.passScore),
      maxRounds: values.maxRounds === "" ? undefined : Number(values.maxRounds),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("taskCreate.title")}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel>{t("taskCreate.name")}</FormLabel>
                    <FormControl>
                      <Input placeholder={t("taskCreate.namePlaceholder")} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="deadline"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel>{t("taskCreate.deadline")}</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="passScore"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel>{t("taskCreate.passScore")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        placeholder={t("taskCreate.passScorePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="maxRounds"
                render={({ field }) => (
                  <FormItem className="space-y-1.5">
                    <FormLabel>{t("taskCreate.maxRounds")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={50}
                        placeholder={t("taskCreate.maxRoundsPlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="scenarioId"
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabel>{t("taskCreate.scenario")}</FormLabel>
                  <FormControl>
                    <select
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      {...field}
                    >
                      <option value="">{t("taskCreate.scenarioPlaceholder")}</option>
                      {scenarios.filter(isPracticableScenario).map((sc) => (
                        <option key={sc.id} value={sc.id}>
                          {sc.name}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>
                  {t("taskCreate.selectEmployees")}
                  <span className="text-xs text-muted-foreground ml-2">
                    {t("taskCreate.selectedCount", { count: selectedIds.size })}
                  </span>
                </Label>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={selectAllVisible}>
                    {t("taskCreate.selectAllVisible")}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={clearAll}>
                    {t("taskCreate.clearAll")}
                  </Button>
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder={t("taskCreate.memberSearchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="max-h-64 overflow-y-auto rounded border">
                {filteredMembers.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4 text-center">
                    {t("taskCreate.emptyMembers")}
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <tbody>
                      {filteredMembers.map((m) => (
                        <tr
                          key={m.principalId}
                          className="border-t hover:bg-muted/30 cursor-pointer"
                          onClick={() => toggle(m.principalId)}
                        >
                          <td className="w-10 p-2">
                            {/* ★toggle 只挂一处(Z9d 0926):此前行 onClick 与 input onChange 双挂,
                                点复选框本体 = onChange 翻一次 + 冒泡到行再翻一次 = 白点;
                                「第二次取消不掉」正是这个。input 的点击不再冒泡到行,行点击(名字/部门格)照旧。 */}
                            <input
                              type="checkbox"
                              aria-label={m.name ?? String(m.principalId)}
                              checked={selectedIds.has(m.principalId)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggle(m.principalId)}
                            />
                          </td>
                          <td className="p-2">{m.name ?? "—"}</td>
                          <td className="p-2 text-muted-foreground">{m.department ?? "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t("taskCreate.cancel")}
              </Button>
              <Button type="submit" disabled={selectedIds.size === 0 || isPending}>
                {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {t("taskCreate.dispatch", { count: selectedIds.size })}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
